import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Leaf, ArrowRight, ArrowLeft, RotateCcw, Sparkles, ChevronRight, ShoppingCart, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";
import { API, useCart, useAuth } from "@/App";
import { toast } from "sonner";
import { LoginPromptDialog } from "@/components/LoginPromptDialog";

const QUESTIONS = [
  {
    question: "What is your main goal?",
    options: [
      { label: "Boost immunity", value: "immunity" },
      { label: "Improve digestion", value: "digestion" },
      { label: "Increase daily nutrition", value: "nutrition" },
      { label: "Add flavor to dishes", value: "flavor" },
      { label: "General healthy lifestyle", value: "lifestyle" },
    ],
  },
  {
    question: "How do you usually consume greens?",
    options: [
      { label: "Salads", value: "salads" },
      { label: "Indian dishes (dal, sabzi, roti)", value: "indian" },
      { label: "Smoothies / juices", value: "smoothies" },
      { label: "Sandwiches / wraps", value: "sandwiches" },
      { label: "Garnish for dishes", value: "garnish" },
    ],
  },
  {
    question: "What flavor do you prefer?",
    options: [
      { label: "Mild and fresh", value: "mild" },
      { label: "Nutty and crunchy", value: "nutty" },
      { label: "Peppery and spicy", value: "peppery" },
      { label: "Sweet and delicate", value: "sweet" },
    ],
  },
  {
    question: "What type of diet do you follow?",
    options: [
      { label: "Vegetarian", value: "vegetarian" },
      { label: "Vegan", value: "vegan" },
      { label: "Fitness focused", value: "fitness" },
      { label: "General healthy eating", value: "general" },
    ],
  },
  {
    question: "How often do you plan to eat microgreens?",
    options: [
      { label: "Daily", value: "daily" },
      { label: "3-4 times a week", value: "frequent" },
      { label: "Occasionally", value: "occasional" },
    ],
  },
];

const PRODUCT_META = {
  "Broccoli": {
    benefits: ["High in antioxidants", "Supports immunity", "Rich in sulforaphane"],
    flavor: "Mild, slightly bitter",
    dishes: ["Salads", "Sandwiches", "Dal toppings", "Smoothies"],
  },
  "Radish Red": {
    benefits: ["Aids digestion", "Detoxifying", "High in vitamin C"],
    flavor: "Peppery and crunchy",
    dishes: ["Tacos", "Roti wraps", "Raita", "Garnish"],
  },
  "Sunflower": {
    benefits: ["Rich in protein", "High in zinc", "Boosts energy"],
    flavor: "Nutty and crunchy",
    dishes: ["Salads", "Sandwiches", "Stir-fry", "Smoothie bowls"],
  },
  "Pea Shoots": {
    benefits: ["High in vitamin A & C", "Sweet taste", "Great for beginners"],
    flavor: "Sweet and delicate",
    dishes: ["Salads", "Pasta", "Soups", "Stir-fry"],
  },
  "Mustard": {
    benefits: ["Anti-inflammatory", "Metabolism booster", "Rich in fiber"],
    flavor: "Spicy and bold",
    dishes: ["Indian dishes", "Sandwiches", "Egg dishes", "Soups"],
  },
  "Amaranthus": {
    benefits: ["Colorful nutrition", "High in iron", "Rich in antioxidants"],
    flavor: "Mild earthy taste",
    dishes: ["Salads", "Rice bowls", "Garnish", "Smoothies"],
  },
  "Wheatgrass": {
    benefits: ["Detoxifying", "Boosts energy", "Rich in chlorophyll"],
    flavor: "Sweet, grassy",
    dishes: ["Juices", "Smoothies", "Shots"],
  },
  "Basil": {
    benefits: ["Aromatic", "Anti-bacterial", "Rich in vitamin K"],
    flavor: "Aromatic, sweet-peppery",
    dishes: ["Italian dishes", "Salads", "Soups", "Indian cuisine"],
  },
  "Fenugreek": {
    benefits: ["Blood sugar support", "Rich in iron", "Digestive health"],
    flavor: "Slightly bitter, earthy",
    dishes: ["Indian curries", "Parathas", "Dal", "Salads"],
  },
  "Kale": {
    benefits: ["Superfood", "High in vitamin K", "Bone health"],
    flavor: "Mild, slightly sweet",
    dishes: ["Salads", "Smoothies", "Wraps", "Bowls"],
  },
};

function getRecommendations(answers, products) {
  const scores = {};
  Object.keys(PRODUCT_META).forEach(name => { scores[name] = 0; });

  const goal = answers[0];
  if (goal === "immunity") { scores["Broccoli"] += 3; scores["Wheatgrass"] += 2; scores["Amaranthus"] += 1; }
  if (goal === "digestion") { scores["Radish Red"] += 3; scores["Fenugreek"] += 2; scores["Mustard"] += 1; }
  if (goal === "nutrition") { scores["Amaranthus"] += 3; scores["Broccoli"] += 2; scores["Kale"] += 2; }
  if (goal === "flavor") { scores["Basil"] += 3; scores["Mustard"] += 2; scores["Radish Red"] += 1; }
  if (goal === "lifestyle") { scores["Sunflower"] += 2; scores["Pea Shoots"] += 2; scores["Broccoli"] += 1; }

  const consumption = answers[1];
  if (consumption === "salads") { scores["Sunflower"] += 2; scores["Pea Shoots"] += 2; }
  if (consumption === "indian") { scores["Fenugreek"] += 2; scores["Mustard"] += 2; scores["Radish Red"] += 1; }
  if (consumption === "smoothies") { scores["Wheatgrass"] += 3; scores["Broccoli"] += 1; scores["Kale"] += 1; }
  if (consumption === "sandwiches") { scores["Sunflower"] += 2; scores["Radish Red"] += 2; }
  if (consumption === "garnish") { scores["Amaranthus"] += 2; scores["Basil"] += 2; }

  const flavor = answers[2];
  if (flavor === "mild") { scores["Broccoli"] += 2; scores["Pea Shoots"] += 2; scores["Kale"] += 1; }
  if (flavor === "nutty") { scores["Sunflower"] += 3; }
  if (flavor === "peppery") { scores["Radish Red"] += 3; scores["Mustard"] += 2; }
  if (flavor === "sweet") { scores["Pea Shoots"] += 3; scores["Basil"] += 1; }

  const diet = answers[3];
  if (diet === "fitness") { scores["Wheatgrass"] += 2; scores["Sunflower"] += 2; scores["Broccoli"] += 1; }
  if (diet === "vegan") { scores["Sunflower"] += 1; scores["Amaranthus"] += 1; }

  const frequency = answers[4];
  if (frequency === "daily") { scores["Broccoli"] += 1; scores["Sunflower"] += 1; }
  if (frequency === "occasional") { scores["Basil"] += 1; scores["Pea Shoots"] += 1; }

  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);

  // Map to actual product data from DB
  return sorted.slice(0, 3).map(([name]) => {
    const dbProduct = products.find(p => p.name === name);
    const meta = PRODUCT_META[name] || {};
    return {
      name,
      slug: dbProduct?.slug || name.toLowerCase().replace(/\s+/g, "-"),
      image: dbProduct?.image || "",
      ...meta,
    };
  });
}

export function MicrogreenQuiz({ products = [] }) {
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [results, setResults] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const { addToCart } = useCart();
  const { user } = useAuth();

  const progress = results ? 100 : ((step / QUESTIONS.length) * 100);

  const handleAnswer = (value) => {
    const newAnswers = [...answers, value];
    setAnswers(newAnswers);
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      setResults(getRecommendations(newAnswers, products));
    }
  };

  const goBack = () => {
    if (step > 0) {
      setStep(step - 1);
      setAnswers(answers.slice(0, -1));
    }
  };

  const reset = () => {
    setStarted(false);
    setStep(0);
    setAnswers([]);
    setResults(null);
    setQuantities({});
  };

  const handleAddToCart = async (productId) => {
    if (!user) { setShowLoginPrompt(true); return; }
    try {
      await addToCart(productId, 1);
      setQuantities(prev => ({ ...prev, [productId]: 1 }));
      toast.success("Added to cart!");
    } catch { toast.error("Failed to add to cart"); }
  };

  const handleQtyChange = async (productId, delta) => {
    const current = quantities[productId] || 0;
    const newQty = current + delta;
    if (newQty <= 0) {
      setQuantities(prev => { const n = { ...prev }; delete n[productId]; return n; });
      toast.info("Removed from cart");
      return;
    }
    try {
      await addToCart(productId, delta);
      setQuantities(prev => ({ ...prev, [productId]: newQty }));
    } catch { toast.error("Failed to update cart"); }
  };

  // ============ START SCREEN ============
  if (!started) {
    return (
      <section className="py-14 md:py-20 bg-praroha-forest" data-testid="microgreen-quiz-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white/80 text-sm font-medium rounded-full mb-5">
              <Sparkles className="w-4 h-4" /> Personalized for You
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Find Your Microgreen
            </h2>
            <p className="text-white/60 max-w-lg mx-auto mb-8">
              Answer a few quick questions to discover the best microgreens for your meals, health goals, and taste preferences.
            </p>
            <Button
              size="lg"
              className="bg-white text-praroha-forest hover:bg-white/90 rounded-full px-8 py-6 text-base font-semibold shadow-lg"
              onClick={() => setStarted(true)}
              data-testid="start-quiz-btn"
            >
              Take the Quiz <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-white/30 text-xs mt-4">Takes less than 30 seconds</p>
          </motion.div>
        </div>
      </section>
    );
  }

  // ============ RESULTS SCREEN ============
  if (results) {
    return (
      <section className="py-14 md:py-20 bg-praroha-forest" data-testid="quiz-results-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-praroha-sage/20 text-praroha-sage text-sm font-medium rounded-full mb-4">
              <Sparkles className="w-4 h-4" /> Your Results
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
              Your Microgreen Match
            </h2>
            <p className="text-white/50">Based on your preferences, here are our top picks for you</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5 mb-8">
            {results.map((rec, i) => {
              const dbProduct = products.find(p => p.slug === rec.slug);
              const productId = dbProduct?.id;
              const price = dbProduct?.price || 180;
              const originalPrice = dbProduct?.price || 180;

              return (
                <motion.div
                  key={rec.slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.15 }}
                  className="flex"
                >
                  <Card className={`backdrop-blur-sm overflow-hidden flex flex-col w-full ${i === 0 ? 'bg-white/15 border-praroha-sage/40 ring-1 ring-praroha-sage/30' : 'bg-white/10 border-white/10'}`} data-testid={`quiz-result-${i}`}>
                    <Link to={`/products/${rec.slug}`} className="block relative">
                      <div className="aspect-square overflow-hidden bg-white/5">
                        <img src={rec.image} alt={rec.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                      </div>
                      {i === 0 && (
                        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-praroha-terracotta text-white text-xs font-bold rounded-full shadow-lg" data-testid="best-match-badge">
                          <Sparkles className="w-3 h-3" /> Best Match
                        </span>
                      )}
                    </Link>
                    <CardContent className="p-4 flex-1 flex flex-col">
                      <Link to={`/products/${rec.slug}`}>
                        <h3 className="font-heading text-base font-bold text-white mb-1 hover:text-praroha-sage transition-colors">{rec.name}</h3>
                      </Link>
                      <p className="text-white/90 text-xs mb-2 font-medium">{rec.flavor}</p>

                      <ul className="space-y-1 mb-3">
                        {rec.benefits?.slice(0, 3).map((b, j) => (
                          <li key={j} className="text-xs text-white/90 flex items-center gap-1.5">
                            <Leaf className="w-3 h-3 text-praroha-sage shrink-0" /> {b}
                          </li>
                        ))}
                      </ul>

                      <div className="flex flex-wrap gap-1 mb-4">
                        {rec.dishes?.slice(0, 3).map((d, j) => (
                          <span key={j} className="px-2 py-0.5 bg-white/20 text-white/90 text-[10px] rounded-full">{d}</span>
                        ))}
                      </div>

                      {/* Price + Add to Cart */}
                      <div className="flex items-center justify-between mt-auto pt-3 border-t border-white/15">
                        <span className="font-semibold text-white text-sm">
                            <>₹{price}</>
                          <span className="text-xs font-normal text-white/50"> / {dbProduct?.weight || '50g'}</span>
                        </span>
                        {productId && quantities[productId] ? (
                          <div className="inline-flex items-center bg-white rounded-full shadow-md" data-testid={`quiz-stepper-${rec.slug}`}>
                            <button
                              className="w-8 h-8 flex items-center justify-center text-praroha-forest/60 hover:text-praroha-forest active:scale-90 transition-all"
                              onClick={() => handleQtyChange(productId, -1)}
                            >
                              <Minus className="w-3.5 h-3.5" strokeWidth={2.5} />
                            </button>
                            <span className="w-5 text-center text-sm font-bold text-praroha-forest tabular-nums">
                              {quantities[productId]}
                            </span>
                            <button
                              className="w-8 h-8 flex items-center justify-center text-praroha-forest/60 hover:text-praroha-forest active:scale-90 transition-all"
                              onClick={() => handleQtyChange(productId, 1)}
                            >
                              <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
                            </button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            className="bg-white text-praroha-forest hover:bg-white/90 rounded-full text-xs px-4 h-8 font-semibold"
                            onClick={() => productId && handleAddToCart(productId)}
                            data-testid={`quiz-add-${rec.slug}`}
                          >
                            <ShoppingCart className="w-3.5 h-3.5 mr-1" /> Add
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/products">
              <Button variant="outline" className="rounded-full border-white/20 text-white hover:bg-white/10 px-6">
                Explore All Microgreens
              </Button>
            </Link>
            <Button variant="ghost" onClick={reset} className="text-white/40 hover:text-white hover:bg-white/10 rounded-full" data-testid="retake-quiz-btn">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Retake Quiz
            </Button>
          </div>
        </div>
        <LoginPromptDialog open={showLoginPrompt} onOpenChange={setShowLoginPrompt} />
      </section>
    );
  }

  // ============ QUIZ QUESTIONS ============
  const q = QUESTIONS[step];

  return (
    <section className="py-14 md:py-20 bg-praroha-forest" data-testid="quiz-active-section">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2.5">
            <span className="text-sm text-white/60 font-medium">Step {step + 1} of {QUESTIONS.length}</span>
            <span className="text-sm text-white/60 font-medium">{Math.round(progress)}%</span>
          </div>
          <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-praroha-sage rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.25 }}
          >
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-6 text-center">
              {q.question}
            </h3>

            <div className="grid gap-3">
              {q.options.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleAnswer(opt.value)}
                  className="w-full text-left bg-white/5 hover:bg-white/15 border border-white/10 hover:border-white/25 rounded-xl px-5 py-4 flex items-center gap-4 transition-all group"
                  data-testid={`quiz-option-${opt.value}`}
                >
                  <span className="font-medium text-white/80 group-hover:text-white transition-colors text-sm md:text-base flex-1">
                    {opt.label}
                  </span>
                  <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-white/60 transition-colors shrink-0" />
                </button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        {step > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6 text-center">
            <Button variant="ghost" onClick={goBack} className="text-white/30 hover:text-white hover:bg-white/10 rounded-full text-sm" data-testid="quiz-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </motion.div>
        )}
      </div>
    </section>
  );
}
